<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InstagramAutoReplyAPI from '../../../../api/instagramAutoReply';

const emit = defineEmits(['close']);

const { t } = useI18n();

const commentText = ref('');
const postId = ref('');
const username = ref('sample_user');
const loading = ref(false);
const testResult = ref(null);

const closeModal = () => {
  emit('close');
};

const handleTest = async () => {
  if (!commentText.value.trim()) return;

  loading.value = true;
  testResult.value = null;
  try {
    const res = await InstagramAutoReplyAPI.testMatch({
      comment_text: commentText.value.trim(),
      post_id: postId.value.trim() || null,
      username: username.value.trim() || 'sample_user',
    });
    testResult.value = res.data;
  } catch (error) {
    testResult.value = {
      matched: false,
      error: error.message,
    };
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto max-h-[85vh] overflow-y-auto px-6 py-4">
    <woot-modal-header
      :header-title="$t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.TITLE')"
      :header-content="$t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.DESC')"
    />

    <div class="flex flex-col w-full gap-4 mt-3">
      <!-- Sample Comment Text -->
      <div class="w-full">
        <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.COMMENT_TEXT.LABEL') }}
        </label>
        <input
          v-model="commentText"
          type="text"
          class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          :placeholder="$t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.COMMENT_TEXT.PLACEHOLDER')"
          @keyup.enter="handleTest"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <!-- Username -->
        <div>
          <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.USERNAME.LABEL') }}
          </label>
          <input
            v-model="username"
            type="text"
            class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            placeholder="sample_user"
          />
        </div>
        <!-- Post ID -->
        <div>
          <label class="block mb-1 text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.POST_ID.LABEL') }}
          </label>
          <input
            v-model="postId"
            type="text"
            class="w-full px-3 py-2 border rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono text-sm"
            placeholder="1789..."
          />
        </div>
      </div>

      <div class="flex justify-end">
        <NextButton
          :is-loading="loading"
          :disabled="!commentText.trim()"
          :label="$t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.TEST_BUTTON')"
          @click="handleTest"
        />
      </div>

      <!-- Result Box -->
      <div v-if="testResult" class="p-4 mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        <div v-if="testResult.matched" class="flex flex-col gap-3">
          <div class="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold text-sm">
            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
            {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.MATCH_FOUND') }}:
            <span class="text-slate-900 dark:text-slate-100">{{ testResult.rule.name }}</span>
          </div>

          <!-- Reply Preview -->
          <div class="p-3 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
              💬 {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.COMMENT_REPLY_OUTPUT') }}
            </div>
            <div class="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
              {{ testResult.rendered_reply }}
            </div>
          </div>

          <!-- DM Preview -->
          <div v-if="testResult.rendered_dm" class="p-3 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
              📩 {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.DM_OUTPUT') }}
            </div>
            <div class="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap mb-2">
              {{ testResult.rendered_dm }}
            </div>

            <!-- Buttons in DM -->
            <div
              v-if="testResult.rendered_buttons && testResult.rendered_buttons.length"
              class="flex flex-col gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-700/60"
            >
              <div
                v-for="(btn, idx) in testResult.rendered_buttons"
                :key="idx"
                class="flex items-center justify-center py-2 px-3 text-xs font-medium text-woot-600 dark:text-woot-300 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-2xs text-center"
              >
                <span>{{ btn.type === 'web_url' ? '🔗 ' : '💬 ' }} {{ btn.title }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-sm text-rose-500 dark:text-rose-400 font-medium">
          ⚠️ {{ $t('INSTAGRAM_AUTO_REPLY.TEST_MODAL.NO_MATCH') }}
        </div>
      </div>
    </div>
  </div>
</template>
